import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Dimensions, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { useNavigate } from 'react-router-dom';
import { GlobalStateContext } from '../../components/general/GlobalStateProvider';
import DataOps from '../../data/DataOps';

const WIDTH = Dimensions.get('window').width;
const PAGE_HORIZONTAL_PADDING = WIDTH >= 1200 ? 100 : 24;

const setupSteps = [
    {
        icon: 'clipboard-file-outline',
        title: 'Upload licenses and permits',
        description: 'Provide required documentation so your camp can be presented with stronger trust and compliance signals.',
    },
    {
        icon: 'map-marker-outline',
        title: 'Add your camp address',
        description: 'Set the camp location so families can discover your program in the right area.',
    },
    {
        icon: 'shape-outline',
        title: 'Choose camp categories',
        description: 'Select the camp types that best describe your program and help parents find the right fit.',
    },
    {
        icon: 'text-box-check-outline',
        title: 'Complete your camp profile',
        description: 'Add your camp name, contact details, and a strong description that explains your program clearly.',
    },
    {
        icon: 'image-multiple-outline',
        title: 'Add camp photos',
        description: 'Upload images that help families understand the environment, activities, and experience you offer.',
    },
];

const EligibilityRow = ({
    title,
    description,
    passed,
    isPending = false,
}: {
    title: string;
    description: string;
    passed: boolean;
    isPending?: boolean;
}) => (
    <View style={styles.eligibilityRow}>
        <View style={[
            styles.eligibilityIconWrap,
            isPending
                ? styles.pendingIconWrap
                : passed
                    ? styles.passedIconWrap
                    : styles.failedIconWrap,
        ]}>
            <MaterialCommunityIcons
                name={isPending ? 'clock-outline' : passed ? 'check' : 'close'}
                size={18}
                color={isPending ? '#B45309' : passed ? '#15803D' : '#B91C1C'}
            />
        </View>
        <View style={styles.eligibilityCopy}>
            <Text style={styles.eligibilityTitle}>{title}</Text>
            <Text style={styles.eligibilityDescription}>{description}</Text>
        </View>
    </View>
);

const HostingCreateCampStart = () => {
    const navigate = useNavigate();
    const { user, isLoadingUser, setShowLoginModal } = useContext(GlobalStateContext);
    const [campCount, setCampCount] = useState<number | null>(null);

    useEffect(() => {
        const loadCampCount = async () => {
            if (!user?.userId || !user?.accountType) {
                setCampCount(null);
                return;
            }

            try {
                const collection = DataOps.getCollectionNameForAccountType(user.accountType);
                const userData = await DataOps.fetchDataFromFirebase(collection, user.userId);
                const camps = Array.isArray(userData?.camps) ? userData.camps : [];
                setCampCount(camps.length);
            } catch {
                setCampCount(0);
            }
        };

        loadCampCount();
    }, [user?.userId, user?.accountType]);

    const isHostAccount = user?.accountType === 'host';
    const hasExistingCamp = (campCount ?? 0) > 0;
    const isCheckingEligibility = isLoadingUser || (Boolean(user?.userId) && campCount === null);

    const canCreateCamp = useMemo(() => {
        return Boolean(user?.userId) && isHostAccount && !hasExistingCamp && !isCheckingEligibility;
    }, [user?.userId, isHostAccount, hasExistingCamp, isCheckingEligibility]);

    const handlePrimaryAction = () => {
        if (!user) {
            setShowLoginModal(true);
            return;
        }

        if (canCreateCamp) {
            navigate('/hosting-create-camp/setup');
            return;
        }

        if (hasExistingCamp) {
            navigate('/hosting-manage-camp');
        }
    };

    const primaryLabel = !user
        ? 'Log in to check eligibility'
        : canCreateCamp
            ? 'Continue to setup wizard'
            : hasExistingCamp
                ? 'Manage your existing camp'
                : 'Host account required';

    const eligibilitySummary = !user
        ? 'Sign in with a host account to create a camp.'
        : isCheckingEligibility
            ? 'Checking your eligibility to create a camp.'
            : canCreateCamp
                ? 'You are eligible to create a camp and continue to setup.'
                : hasExistingCamp
                    ? 'This account already has a camp. Only one SnapCamp can be created per account.'
                    : 'Only users signed in with a host account can create a camp.';

    return (
        <ScrollView style={styles.page} contentContainerStyle={styles.pageContent}>
            <View style={styles.container}>
                <View style={styles.hero}>
                    <View style={styles.heroBadge}>
                        <MaterialCommunityIcons name="home-plus-outline" size={16} color="#1D4ED8" />
                        <Text style={styles.heroBadgeText}>Create a Camp</Text>
                    </View>

                    <Text style={styles.heroTitle}>Get started before you create your SnapCamp.</Text>
                    <Text style={styles.heroSubtitle}>
                        This page walks you through the high-level setup steps and checks whether your account is eligible to create a camp.
                    </Text>
                </View>

                <View style={styles.sectionCard}>
                    <Text style={styles.sectionTitle}>Eligibility check</Text>
                    <Text style={styles.sectionSubtitle}>{eligibilitySummary}</Text>

                    <View style={styles.eligibilityList}>
                        <EligibilityRow
                            title="Signed in"
                            description="You must be logged in before you can create a camp."
                            passed={Boolean(user?.userId)}
                            isPending={isLoadingUser}
                        />
                        <EligibilityRow
                            title="Host account"
                            description="Only host accounts are eligible to create and manage camps."
                            passed={isHostAccount}
                            isPending={isCheckingEligibility}
                        />
                        <EligibilityRow
                            title="No existing SnapCamp"
                            description="Each eligible account can create one SnapCamp."
                            passed={!hasExistingCamp}
                            isPending={Boolean(user?.userId) && campCount === null}
                        />
                    </View>

                    <Pressable
                        style={[
                            styles.primaryButton,
                            (!user || canCreateCamp || hasExistingCamp) ? null : styles.disabledButton,
                        ]}
                        onPress={handlePrimaryAction}
                        disabled={Boolean(user) && !canCreateCamp && !hasExistingCamp}
                    >
                        <Text style={styles.primaryButtonText}>{primaryLabel}</Text>
                        <MaterialCommunityIcons name="arrow-right" size={18} color="#FFFFFF" />
                    </Pressable>
                </View>

                <View style={styles.sectionCard}>
                    <Text style={styles.sectionTitle}>High-level setup steps</Text>
                    <Text style={styles.sectionSubtitle}>
                        Once you are eligible, you will go through the setup wizard to complete the key information below.
                    </Text>

                    <View style={styles.stepList}>
                        {setupSteps.map((step) => (
                            <View key={step.title} style={styles.stepRow}>
                                <View style={styles.stepIconWrap}>
                                    <MaterialCommunityIcons name={step.icon as any} size={20} color="#1D4ED8" />
                                </View>
                                <View style={styles.stepCopy}>
                                    <Text style={styles.stepTitle}>{step.title}</Text>
                                    <Text style={styles.stepDescription}>{step.description}</Text>
                                </View>
                            </View>
                        ))}
                    </View>
                </View>
            </View>
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    page: {
        flex: 1,
        backgroundColor: '#F8FAFC',
    },
    pageContent: {
        paddingTop: 36,
        paddingBottom: 72,
        paddingHorizontal: PAGE_HORIZONTAL_PADDING,
    },
    container: {
        width: '100%',
        gap: 28,
    },
    hero: {
        backgroundColor: '#FFFFFF',
        borderRadius: 28,
        padding: 32,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        shadowColor: '#0F172A',
        shadowOpacity: 0.06,
        shadowRadius: 18,
        shadowOffset: { width: 0, height: 8 },
    },
    heroBadge: {
        alignSelf: 'flex-start',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 999,
        backgroundColor: '#DBEAFE',
        marginBottom: 18,
    },
    heroBadgeText: {
        fontSize: 13,
        fontWeight: '700',
        color: '#1D4ED8',
    },
    heroTitle: {
        fontSize: 36,
        lineHeight: 44,
        fontWeight: '800',
        color: '#0F172A',
        marginBottom: 14,
    },
    heroSubtitle: {
        fontSize: 17,
        lineHeight: 28,
        color: '#475569',
    },
    sectionCard: {
        backgroundColor: '#FFFFFF',
        borderRadius: 28,
        padding: 32,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        shadowColor: '#0F172A',
        shadowOpacity: 0.05,
        shadowRadius: 16,
        shadowOffset: { width: 0, height: 6 },
    },
    sectionTitle: {
        fontSize: 30,
        lineHeight: 38,
        fontWeight: '800',
        color: '#0F172A',
        marginBottom: 10,
    },
    sectionSubtitle: {
        fontSize: 16,
        lineHeight: 26,
        color: '#475569',
        marginBottom: 22,
    },
    eligibilityList: {
        gap: 16,
        marginBottom: 24,
    },
    eligibilityRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 14,
        backgroundColor: '#F8FAFC',
        borderRadius: 18,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        padding: 18,
    },
    eligibilityIconWrap: {
        width: 36,
        height: 36,
        borderRadius: 999,
        alignItems: 'center',
        justifyContent: 'center',
    },
    passedIconWrap: {
        backgroundColor: '#DCFCE7',
    },
    failedIconWrap: {
        backgroundColor: '#FEE2E2',
    },
    pendingIconWrap: {
        backgroundColor: '#FEF3C7',
    },
    eligibilityCopy: {
        flex: 1,
    },
    eligibilityTitle: {
        fontSize: 17,
        lineHeight: 24,
        fontWeight: '700',
        color: '#0F172A',
        marginBottom: 4,
    },
    eligibilityDescription: {
        fontSize: 15,
        lineHeight: 24,
        color: '#475569',
    },
    primaryButton: {
        alignSelf: 'flex-start',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        backgroundColor: '#0F172A',
        paddingHorizontal: 18,
        paddingVertical: 14,
        borderRadius: 999,
    },
    disabledButton: {
        backgroundColor: '#94A3B8',
    },
    primaryButtonText: {
        color: '#FFFFFF',
        fontSize: 15,
        fontWeight: '700',
    },
    stepList: {
        gap: 16,
    },
    stepRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 14,
        paddingBottom: 16,
        borderBottomWidth: 1,
        borderBottomColor: '#E2E8F0',
    },
    stepIconWrap: {
        width: 40,
        height: 40,
        borderRadius: 14,
        backgroundColor: '#DBEAFE',
        alignItems: 'center',
        justifyContent: 'center',
    },
    stepCopy: {
        flex: 1,
    },
    stepTitle: {
        fontSize: 18,
        lineHeight: 24,
        fontWeight: '700',
        color: '#0F172A',
        marginBottom: 4,
    },
    stepDescription: {
        fontSize: 15,
        lineHeight: 24,
        color: '#475569',
    },
});

export default HostingCreateCampStart;
